if (!customElements.get('coin-media-viewer')) {
  customElements.define(
    'coin-media-viewer',
    class CoinsMediaViewer extends HTMLElement {
      constructor() {
        super();
        this.index = 0;
        this.scale = 1;
        this.maxScale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.pointers = new Map();
        this.preloaded = new Map();
        this.lastTap = null;
        this.backdropPress = null;
        this.loadToken = 0;
        this.resizeFrame = null;
        this.buttonZoomStep = 0.25;
        this.wheelZoomIntensity = 0.00118;
        this.viewerScaleLimit = 3;
        this.minimumUsefulScale = 2;
        this.maximumPixelUpscale = 2;
      }

      connectedCallback() {
        if (!this.moved && this.parentElement !== document.body) {
          this.moved = true;
          document.body.appendChild(this);
          return;
        }
        if (this.initialized) return;
        this.initialized = true;

        this.dialog = this.querySelector('[role="dialog"]');
        this.stage = this.querySelector('[data-viewer-stage]');
        this.canvas = this.querySelector('[data-viewer-canvas]');
        this.image = this.querySelector('[data-viewer-image]');
        this.loader = this.querySelector('[data-viewer-loader]');
        this.counter = this.querySelector('.coin-media-viewer__counter');
        this.current = this.querySelector('[data-viewer-current]');
        this.total = this.querySelector('[data-viewer-total]');
        this.status = this.querySelector('[data-viewer-status]');
        this.zoomValue = this.querySelector('[data-viewer-zoom]');
        this.prevButton = this.querySelector('[data-viewer-prev]');
        this.nextButton = this.querySelector('[data-viewer-next]');
        this.zoomOutButton = this.querySelector('[data-viewer-zoom-out]');
        this.zoomInButton = this.querySelector('[data-viewer-zoom-in]');
        this.resetButton = this.querySelector('[data-viewer-reset]');
        this.closeButton = this.querySelector('[data-viewer-close]');
        this.dataContainer = this.querySelector('[data-viewer-data]');

        this.readItems();
        this.bindEvents();
        this.dataObserver = new MutationObserver(() => this.readItems());
        this.dataObserver.observe(this.dataContainer, { childList: true, subtree: true, characterData: true });
      }

      bindEvents() {
        this.closeButton.addEventListener('click', () => this.hide());
        this.prevButton.addEventListener('click', () => this.go(-1));
        this.nextButton.addEventListener('click', () => this.go(1));
        this.zoomOutButton.addEventListener('click', () => this.setScale(this.scale - this.buttonZoomStep));
        this.zoomInButton.addEventListener('click', () => this.setScale(this.scale + this.buttonZoomStep));
        this.resetButton.addEventListener('click', () => this.resetZoom(true));
        this.addEventListener('keydown', (event) => this.onKeyDown(event));

        this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.stage.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.stage.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.stage.addEventListener('pointercancel', (event) => this.onPointerUp(event, true));
        this.stage.addEventListener('lostpointercapture', (event) => this.onLostPointer(event));
        this.stage.addEventListener('dblclick', (event) => {
          event.preventDefault();
          this.toggleDoubleZoom(event.clientX, event.clientY);
        });
        this.stage.addEventListener(
          'wheel',
          (event) => this.onWheel(event),
          { passive: false }
        );
        this.dialog.addEventListener('pointerdown', (event) => this.onBackdropPointerDown(event));
        this.dialog.addEventListener('pointerup', (event) => this.onBackdropPointerUp(event));
        this.dialog.addEventListener('pointercancel', () => { this.backdropPress = null; });

        this.boundResize = () => {
          if (!this.hasAttribute('open')) return;
          cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = requestAnimationFrame(() => this.resetZoom(false));
        };
        window.addEventListener('resize', this.boundResize);
        window.addEventListener('orientationchange', this.boundResize);
        window.visualViewport?.addEventListener('resize', this.boundResize);
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.cancelGesture();
        });
        window.addEventListener('blur', () => this.cancelGesture());
      }

      readItems() {
        const source = this.dataContainer?.querySelector('script[type="application/json"]');
        try {
          this.items = JSON.parse(source?.textContent || '[]');
        } catch (error) {
          this.items = [];
          console.error('Coins2140 image viewer: invalid media data.', error);
        }
        this.total.textContent = String(this.items.length);
        this.classList.toggle('is-single', this.items.length <= 1);
        if (this.index >= this.items.length) this.index = Math.max(0, this.items.length - 1);
        this.updateControls();
      }

      show(opener) {
        if (!this.items.length) return;
        this.openedBy = opener;
        const mediaId = String(opener?.dataset.mediaId || '');
        const requestedIndex = this.items.findIndex((item) => String(item.id) === mediaId);
        this.index = requestedIndex >= 0 ? requestedIndex : this.index;
        this.lockPage();
        this.removeAttribute('hidden');
        this.setAttribute('open', '');
        this.setBackgroundInert(true);
        this.showIndex(this.index, { syncGallery: false, usePlaceholder: true });
        this.closeButton.focus({ preventScroll: true });
      }

      hide() {
        if (!this.hasAttribute('open')) return;
        this.backdropPress = null;
        this.cancelGesture();
        this.removeAttribute('open');
        this.setAttribute('hidden', '');
        this.setBackgroundInert(false);
        this.unlockPage();
        const opener = this.openedBy;
        this.openedBy = null;
        requestAnimationFrame(() => opener?.focus?.({ preventScroll: true }));
      }

      lockPage() {
        this.savedScroll = { x: window.scrollX, y: window.scrollY };
        this.previewBar = document.getElementById('PBarNextFrame');
        if (this.previewBar) {
          this.previewBarDisplay = this.previewBar.style.display;
          this.previewBar.style.display = 'none';
        }
        this.savedStyles = {
          htmlOverflow: document.documentElement.style.overflow,
          bodyPosition: document.body.style.position,
          bodyTop: document.body.style.top,
          bodyLeft: document.body.style.left,
          bodyRight: document.body.style.right,
          bodyWidth: document.body.style.width,
          bodyOverflow: document.body.style.overflow,
          bodyPaddingRight: document.body.style.paddingRight,
        };
        const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        document.documentElement.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `${-this.savedScroll.y}px`;
        document.body.style.left = `${-this.savedScroll.x}px`;
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        if (scrollbar) document.body.style.paddingRight = `${scrollbar}px`;
      }

      unlockPage() {
        if (!this.savedStyles) return;
        document.documentElement.style.overflow = this.savedStyles.htmlOverflow;
        document.body.style.position = this.savedStyles.bodyPosition;
        document.body.style.top = this.savedStyles.bodyTop;
        document.body.style.left = this.savedStyles.bodyLeft;
        document.body.style.right = this.savedStyles.bodyRight;
        document.body.style.width = this.savedStyles.bodyWidth;
        document.body.style.overflow = this.savedStyles.bodyOverflow;
        document.body.style.paddingRight = this.savedStyles.bodyPaddingRight;
        if (this.previewBar) {
          this.previewBar.style.display = this.previewBarDisplay;
          this.previewBar = null;
          this.previewBarDisplay = null;
        }
        window.scrollTo(this.savedScroll.x, this.savedScroll.y);
        this.savedStyles = null;
      }

      setBackgroundInert(makeInert) {
        if (makeInert) {
          this.inertState = [];
          [...document.body.children].forEach((element) => {
            if (element === this) return;
            this.inertState.push({
              element,
              inert: element.inert,
              ariaHidden: element.getAttribute('aria-hidden'),
            });
            element.inert = true;
            element.setAttribute('aria-hidden', 'true');
          });
          return;
        }
        this.inertState?.forEach(({ element, inert, ariaHidden }) => {
          element.inert = inert;
          if (ariaHidden === null) element.removeAttribute('aria-hidden');
          else element.setAttribute('aria-hidden', ariaHidden);
        });
        this.inertState = null;
      }

      showIndex(index, options = {}) {
        if (!this.items.length) return;
        this.index = Math.max(0, Math.min(this.items.length - 1, index));
        const item = this.items[this.index];
        const token = ++this.loadToken;
        this.loader.classList.add('is-visible');

        if (options.usePlaceholder) {
          const placeholder = this.openedBy?.closest('.product__modal-opener')?.querySelector('img');
          if (placeholder?.currentSrc) {
            this.image.src = placeholder.currentSrc;
            this.image.alt = item.alt || '';
          }
        }

        const pending = this.preloaded.get(item.src) || new Image();
        pending.decoding = 'async';
        const finish = () => {
          if (token !== this.loadToken) return;
          this.image.src = item.src;
          this.image.alt = item.alt || '';
          this.loader.classList.remove('is-visible');
          this.resetZoom(false);
          this.preloadNeighbours();
        };
        if (pending.complete && pending.naturalWidth) finish();
        else {
          pending.onload = finish;
          pending.onerror = () => {
            if (token === this.loadToken) this.loader.classList.remove('is-visible');
          };
          pending.src = item.src;
        }
        this.preloaded.set(item.src, pending);

        this.current.textContent = String(this.index + 1);
        this.status.textContent = `Imagen ${this.index + 1} de ${this.items.length}`;
        this.updateControls();
        if (options.syncGallery !== false) this.syncGallery(item.id);
      }

      preloadNeighbours() {
        [this.index - 1, this.index + 1].forEach((index) => {
          const item = this.items[index];
          if (!item || this.preloaded.has(item.src)) return;
          const preload = new Image();
          preload.decoding = 'async';
          preload.src = item.src;
          this.preloaded.set(item.src, preload);
        });
      }

      syncGallery(mediaId) {
        const gallery = document.getElementById(`MediaGallery-${this.dataset.sectionId}`);
        const galleryMediaId = `${this.dataset.sectionId}-${mediaId}`;
        gallery?.setActiveMedia?.(galleryMediaId, false, true);
        const currentOpener = gallery?.querySelector(`[data-media-id="${galleryMediaId}"] .coin-viewer-trigger`);
        if (currentOpener) this.openedBy = currentOpener;
      }

      go(delta) {
        const target = this.index + delta;
        if (target < 0 || target >= this.items.length) return;
        this.showIndex(target, { syncGallery: true });
      }

      measureFit() {
        const item = this.items[this.index];
        const rect = this.stage.getBoundingClientRect();
        const sourceWidth = Math.max(Number(item?.width) || 0, this.image.naturalWidth || 0, 1);
        const sourceHeight = Math.max(Number(item?.height) || 0, this.image.naturalHeight || 0, 1);
        const ratio = sourceWidth / sourceHeight;
        let fitWidth = rect.width;
        let fitHeight = fitWidth / ratio;
        if (fitHeight > rect.height) {
          fitHeight = rect.height;
          fitWidth = fitHeight * ratio;
        }
        fitWidth = Math.max(1, Math.min(fitWidth, sourceWidth));
        fitHeight = Math.max(1, Math.min(fitHeight, sourceHeight));
        this.fitWidth = fitWidth;
        this.fitHeight = fitHeight;
        this.canvas.style.width = `${fitWidth}px`;
        this.canvas.style.height = `${fitHeight}px`;

        this.maxScale = this.viewerScaleLimit;
        this.positionNavigation();
      }

      positionNavigation() {
        const viewerRect = this.getBoundingClientRect();
        const stageRect = this.stage.getBoundingClientRect();
        const buttonSize = this.prevButton.getBoundingClientRect().width || 48;
        const compact = viewerRect.width <= 749 || viewerRect.height <= 600;
        const edgeInset = compact ? 10 : 12;
        const imageGap = compact ? 6 : 12;
        const stageLeft = stageRect.left - viewerRect.left;
        const stageTop = stageRect.top - viewerRect.top;
        const imageLeft = stageLeft + (stageRect.width - this.fitWidth) / 2;
        const imageRight = imageLeft + this.fitWidth;
        const imageTop = stageTop + (stageRect.height - this.fitHeight) / 2;
        const imageCenterY = imageTop + this.fitHeight / 2;
        const counterHeight = this.counter?.getBoundingClientRect().height || 34;
        const counterGap = compact ? 10 : 12;
        const counterTop = Math.max(compact ? 8 : 12, imageTop - counterHeight - counterGap);
        const maximumLeft = Math.max(edgeInset, viewerRect.width - edgeInset - buttonSize);
        const previousLeft = Math.max(edgeInset, imageLeft - imageGap - buttonSize);
        const nextLeft = Math.min(maximumLeft, imageRight + imageGap);

        this.style.setProperty('--coin-viewer-nav-y', imageCenterY + 'px');
        this.style.setProperty('--coin-viewer-prev-x', previousLeft + 'px');
        this.style.setProperty('--coin-viewer-next-x', nextLeft + 'px');
        this.style.setProperty('--coin-viewer-image-center-x', imageLeft + this.fitWidth / 2 + 'px');
        this.style.setProperty('--coin-viewer-counter-y', counterTop + 'px');
      }

      onBackdropPointerDown(event) {
        if (!this.isDesktopBackdropPointer(event)) return;
        if (this.isProtectedViewerTarget(event.target) || this.isPointOnImage(event.clientX, event.clientY)) return;
        this.backdropPress = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
        };
      }

      onBackdropPointerUp(event) {
        const press = this.backdropPress;
        this.backdropPress = null;
        if (!press || press.pointerId !== event.pointerId) return;
        if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > 8) return;
        if (this.isProtectedViewerTarget(event.target) || this.isPointOnImage(event.clientX, event.clientY)) return;
        this.hide();
      }

      isDesktopBackdropPointer(event) {
        return (
          event.pointerType === 'mouse' &&
          event.button === 0 &&
          window.matchMedia('(hover: hover) and (pointer: fine)').matches
        );
      }

      isProtectedViewerTarget(target) {
        return target instanceof Element && Boolean(target.closest('button, .coin-media-viewer__counter'));
      }

      isPointOnImage(clientX, clientY) {
        const rect = this.canvas.getBoundingClientRect();
        return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
      }

      resetZoom(announce = false) {
        this.measureFit();
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.applyTransform();
        if (announce) this.status.textContent = 'Zoom restablecido al 100 %';
      }

      setScale(nextScale, clientX, clientY, announce = true) {
        const oldScale = this.scale;
        const clamped = Math.max(1, Math.min(this.maxScale, nextScale));
        if (Math.abs(clamped - oldScale) < 0.001) return;
        const rect = this.stage.getBoundingClientRect();
        const focalX = Number.isFinite(clientX) ? clientX - (rect.left + rect.width / 2) : 0;
        const focalY = Number.isFinite(clientY) ? clientY - (rect.top + rect.height / 2) : 0;
        const ratio = clamped / oldScale;
        this.translateX = focalX - (focalX - this.translateX) * ratio;
        this.translateY = focalY - (focalY - this.translateY) * ratio;
        this.scale = clamped;
        this.clampTranslation();
        this.applyTransform();
        if (announce) this.status.textContent = `Zoom ${this.zoomPercent()} %`;
      }

      toggleDoubleZoom(clientX, clientY) {
        if (this.scale < 1.05) {
          this.setScale(2, clientX, clientY);
        } else if (this.scale < this.maxScale - 0.05) {
          this.setScale(this.maxScale, clientX, clientY);
        } else {
          this.resetZoom(true);
        }
      }

      onWheel(event) {
        if (!this.hasAttribute('open')) return;
        event.preventDefault();

        let delta = event.deltaY;
        if (event.deltaMode === 1) delta *= 16;
        else if (event.deltaMode === 2) delta *= Math.max(1, this.stage.clientHeight);

        const controlledDelta = Math.max(-120, Math.min(120, delta));
        if (Math.abs(controlledDelta) < 0.1) return;

        const zoomFactor = Math.exp(-controlledDelta * this.wheelZoomIntensity);
        this.setScale(this.scale * zoomFactor, event.clientX, event.clientY);
      }

      zoomPercent() {
        return Math.round((this.scale * 100) / 5) * 5;
      }

      clampTranslation() {
        const rect = this.stage.getBoundingClientRect();
        const maxX = Math.max(0, (this.fitWidth * this.scale - rect.width) / 2);
        const maxY = Math.max(0, (this.fitHeight * this.scale - rect.height) / 2);
        this.translateX = Math.max(-maxX, Math.min(maxX, this.translateX));
        this.translateY = Math.max(-maxY, Math.min(maxY, this.translateY));
      }

      applyTransform() {
        this.canvas.style.transform = `translate3d(calc(-50% + ${this.translateX}px), calc(-50% + ${this.translateY}px), 0) scale(${this.scale})`;
        this.stage.dataset.canPan = String(this.scale > 1.01);
        this.zoomValue.textContent = `${this.zoomPercent()} %`;
        this.resetButton.setAttribute('aria-label', `Restablecer zoom al 100 %. Zoom actual ${this.zoomPercent()} %`);
        this.updateControls();
      }

      updateControls() {
        if (!this.prevButton) return;
        this.prevButton.disabled = this.index <= 0;
        this.nextButton.disabled = this.index >= this.items.length - 1;
        this.zoomOutButton.disabled = this.scale <= 1.001;
        this.zoomInButton.disabled = this.scale >= this.maxScale - 0.001;
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.stage.setPointerCapture?.(event.pointerId);
        this.pointers.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
          startX: event.clientX,
          startY: event.clientY,
          time: performance.now(),
          type: event.pointerType,
        });
        this.stage.dataset.dragging = 'true';

        if (this.pointers.size === 2) {
          const points = [...this.pointers.values()];
          this.gesture = {
            mode: 'pinch',
            startDistance: this.distance(points[0], points[1]),
            startMidpoint: this.midpoint(points[0], points[1]),
            startScale: this.scale,
            startX: this.translateX,
            startY: this.translateY,
            postPinch: true,
          };
          return;
        }

        this.gesture = {
          mode: this.scale > 1.01 ? 'pan' : 'pending',
          startX: this.translateX,
          startY: this.translateY,
          pointerX: event.clientX,
          pointerY: event.clientY,
          startTime: performance.now(),
          postPinch: false,
        };
      }

      onPointerMove(event) {
        const point = this.pointers.get(event.pointerId);
        if (!point) return;
        point.x = event.clientX;
        point.y = event.clientY;

        if (this.pointers.size >= 2) {
          event.preventDefault();
          const points = [...this.pointers.values()].slice(0, 2);
          if (this.gesture?.mode !== 'pinch') return;
          const currentDistance = this.distance(points[0], points[1]);
          const currentMidpoint = this.midpoint(points[0], points[1]);
          const nextScale = Math.max(1, Math.min(this.maxScale, this.gesture.startScale * currentDistance / Math.max(1, this.gesture.startDistance)));
          const stageRect = this.stage.getBoundingClientRect();
          const centerX = stageRect.left + stageRect.width / 2;
          const centerY = stageRect.top + stageRect.height / 2;
          const initialFocalX = this.gesture.startMidpoint.x - centerX;
          const initialFocalY = this.gesture.startMidpoint.y - centerY;
          const currentFocalX = currentMidpoint.x - centerX;
          const currentFocalY = currentMidpoint.y - centerY;
          const ratio = nextScale / this.gesture.startScale;
          this.scale = nextScale;
          this.translateX = currentFocalX - (initialFocalX - this.gesture.startX) * ratio;
          this.translateY = currentFocalY - (initialFocalY - this.gesture.startY) * ratio;
          this.clampTranslation();
          this.applyTransform();
          return;
        }

        const active = [...this.pointers.values()][0];
        if (!active || !this.gesture) return;
        const dx = active.x - this.gesture.pointerX;
        const dy = active.y - this.gesture.pointerY;

        if (this.gesture.mode === 'pending' && Math.hypot(dx, dy) > 10) {
          if (Math.abs(dx) > Math.abs(dy) * 1.25) this.gesture.mode = 'swipe';
          else this.gesture.mode = 'cancelled';
        }

        if (this.gesture.mode === 'pan') {
          event.preventDefault();
          this.translateX = this.gesture.startX + dx;
          this.translateY = this.gesture.startY + dy;
          this.clampTranslation();
          this.applyTransform();
        } else if (this.gesture.mode === 'swipe') {
          event.preventDefault();
          this.gesture.swipeX = dx;
        }
      }

      onPointerUp(event, cancelled = false) {
        const point = this.pointers.get(event.pointerId);
        if (!point) return;
        point.x = event.clientX;
        point.y = event.clientY;
        const gesture = this.gesture;
        this.pointers.delete(event.pointerId);
        try { this.stage.releasePointerCapture?.(event.pointerId); } catch (error) {}

        if (gesture?.mode === 'pinch') {
          if (this.pointers.size === 1) {
            const remaining = [...this.pointers.values()][0];
            this.gesture = {
              mode: this.scale > 1.01 ? 'pan' : 'cancelled',
              startX: this.translateX,
              startY: this.translateY,
              pointerX: remaining.x,
              pointerY: remaining.y,
              startTime: performance.now(),
              postPinch: true,
            };
          } else {
            this.gesture = null;
            this.status.textContent = `Zoom ${this.zoomPercent()} %`;
          }
          if (!this.pointers.size) delete this.stage.dataset.dragging;
          return;
        }

        if (!this.pointers.size) {
          delete this.stage.dataset.dragging;
          if (!cancelled && gesture?.mode === 'swipe' && !gesture.postPinch) {
            const dx = point.x - gesture.pointerX;
            const elapsed = Math.max(1, performance.now() - gesture.startTime);
            const threshold = Math.max(52, this.stage.clientWidth * 0.145);
            const fastSwipe = Math.abs(dx) >= 32 && Math.abs(dx / elapsed) >= 0.45;
            if (Math.abs(dx) >= threshold || fastSwipe) this.go(dx < 0 ? 1 : -1);
          } else if (!cancelled && gesture?.mode === 'pending' && point.type !== 'mouse' && !gesture.postPinch) {
            this.handleTap(point);
          }
          this.gesture = null;
          this.clampTranslation();
          this.applyTransform();
        }
      }

      onLostPointer(event) {
        if (this.pointers.has(event.pointerId)) this.onPointerUp(event, true);
      }

      handleTap(point) {
        const now = performance.now();
        if (
          this.lastTap &&
          now - this.lastTap.time <= 300 &&
          Math.hypot(point.x - this.lastTap.x, point.y - this.lastTap.y) <= 24
        ) {
          this.toggleDoubleZoom(point.x, point.y);
          this.lastTap = null;
        } else {
          this.lastTap = { x: point.x, y: point.y, time: now };
        }
      }

      cancelGesture() {
        this.pointers.clear();
        this.gesture = null;
        delete this.stage?.dataset.dragging;
      }

      distance(a, b) {
        return Math.hypot(a.x - b.x, a.y - b.y);
      }

      midpoint(a, b) {
        return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      }

      onKeyDown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.hide();
          return;
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(-1);
          return;
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(1);
          return;
        }
        if (event.key !== 'Tab') return;
        const controls = [...this.querySelectorAll('button:not([disabled])')].filter((button) => button.offsetParent !== null);
        if (!controls.length) return;
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}
